import React, { FC, KeyboardEvent, memo, PointerEvent, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import styles from './index.module.scss';
import AIRobot from '../../imgs/AIRobot.gif';
import { useAssistantEmbed, useFloatingAssistantPosition } from './hook';
import {
  animateAssistantIframeClose,
  animateAssistantIframeOpen,
  animateAssistantRobotClose,
  animateAssistantRobotOpen,
  assistantButtonId,
  assistantTransitionStartEvent,
  clampAssistantPosition,
  defaultAssistantSize,
  findAssistantElement,
  getAssistantEmbedController,
  getAssistantWindows,
  getElementPortalContainer,
  syncAssistantPortalContainer,
} from './util';
import type {
  AssistantOffset,
  AssistantPlacement,
  AssistantRobotEffect,
  AssistantSize,
  AssistantTransitionEffect,
} from './util';

export type {
  AssistantOffset,
  AssistantPlacement,
  AssistantPosition,
  AssistantRobotEffect,
  AssistantSize,
  AssistantTransitionContext,
  AssistantTransitionEffect,
} from './util';

export interface AiAssistantProps {
  /** 用于计算初始位置的锚点 */
  anchorRef: React.RefObject<HTMLElement>;
  /** 相对锚点的定位方式 */
  placement?: AssistantPlacement;
  /** 设计稿尺寸，会随 pageScale 缩放 */
  baseSize?: AssistantSize;
  /** 相对锚点的偏移量，会随 pageScale 缩放 */
  offset?: AssistantOffset;
  /** 页面缩放比例 */
  pageScale?: number;
  /** 是否启用 */
  enabled?: boolean;
  /** 额外的 className */
  className?: string;
  /** 开启弹窗时使用的动画或特效方法，未传时使用机器人默认传送效果 */
  openEffect?: AssistantTransitionEffect;
  /** 关闭弹窗时使用的动画或特效方法，未传时使用机器人默认传送效果 */
  closeEffect?: AssistantTransitionEffect;
  /** Embed 开启时仅作用于机器人本体的特效 */
  robotOpenEffect?: AssistantRobotEffect;
  /** Embed 关闭时仅作用于机器人本体的特效 */
  robotCloseEffect?: AssistantRobotEffect;
}

const assistantInstanceChangeEvent = 'ai-assistant-instance-change';
const assistantInitDelay = 500;
const assistantIdleTimeout = 1000;
let assistantInstanceSeed = 0;
let activeAssistantInstanceId = 0;
const mountedAssistantInstanceIds: number[] = [];

// ==================== Component ====================

const AiAssistant: FC<AiAssistantProps> = memo(
  ({
    anchorRef,
    placement = 'anchor',
    baseSize = defaultAssistantSize,
    offset,
    pageScale,
    enabled = true,
    className,
    openEffect,
    closeEffect,
    robotOpenEffect = animateAssistantRobotOpen,
    robotCloseEffect = animateAssistantRobotClose,
  }) => {
    const assistantInstanceIdRef = useRef(0);
    if (!assistantInstanceIdRef.current) {
      assistantInstanceIdRef.current = ++assistantInstanceSeed;
    }
    const buttonHostRef = useRef<HTMLDivElement>(null);
    const assistantDragRef = useRef({
      pointerId: -1,
      startX: 0,
      startY: 0,
      originLeft: 0,
      originTop: 0,
      moved: false,
    });
    const suppressAssistantClickRef = useRef(false);
    const [assistantPortalContainer, setAssistantPortalContainer] = useState<HTMLElement | null>(null);
    const [assistantDragging, setAssistantDragging] = useState(false);
    const [assistantPinned, setAssistantPinned] = useState(false);
    const [isActiveAssistantInstance, setIsActiveAssistantInstance] = useState(false);
    const [assistantInitialized, setAssistantInitialized] = useState(false);
    const [robotAnimationReady, setRobotAnimationReady] = useState(false);
    const [isDocumentVisible, setIsDocumentVisible] = useState(() => !document.hidden);
    const shouldPrepareAssistant = enabled && isActiveAssistantInstance && assistantInitialized && isDocumentVisible;
    const assistantEnabled = shouldPrepareAssistant && robotAnimationReady;
    const { assistantPosition, assistantSize, setAssistantPosition, setAssistantSize } = useFloatingAssistantPosition({
      anchorRef,
      placement,
      baseSize,
      offset,
      pageScale,
      enabled: assistantEnabled,
      pinned: assistantPinned,
    });

    useEffect(() => {
      const instanceId = assistantInstanceIdRef.current;
      const syncActiveState = () => {
        setIsActiveAssistantInstance(activeAssistantInstanceId === instanceId);
      };

      if (!mountedAssistantInstanceIds.includes(instanceId)) {
        mountedAssistantInstanceIds.push(instanceId);
      }
      activeAssistantInstanceId = instanceId;
      window.dispatchEvent(new CustomEvent(assistantInstanceChangeEvent));
      syncActiveState();

      window.addEventListener(assistantInstanceChangeEvent, syncActiveState);
      return () => {
        window.removeEventListener(assistantInstanceChangeEvent, syncActiveState);
        const index = mountedAssistantInstanceIds.indexOf(instanceId);
        if (index > -1) {
          mountedAssistantInstanceIds.splice(index, 1);
        }
        if (activeAssistantInstanceId === instanceId) {
          activeAssistantInstanceId = mountedAssistantInstanceIds[mountedAssistantInstanceIds.length - 1] ?? 0;
          window.dispatchEvent(new CustomEvent(assistantInstanceChangeEvent));
        }
      };
    }, []);

    useEffect(() => {
      if (!enabled || !isActiveAssistantInstance) {
        setAssistantInitialized(false);
        return;
      }

      let idleId: number | undefined;
      const delayId = window.setTimeout(() => {
        if (typeof window.requestIdleCallback === 'function') {
          idleId = window.requestIdleCallback(() => setAssistantInitialized(true), {
            timeout: assistantIdleTimeout,
          });
          return;
        }
        setAssistantInitialized(true);
      }, assistantInitDelay);

      return () => {
        window.clearTimeout(delayId);
        if (idleId !== undefined) {
          window.cancelIdleCallback(idleId);
        }
      };
    }, [enabled, isActiveAssistantInstance]);

    useEffect(() => {
      const handleVisibilityChange = () => {
        setIsDocumentVisible(!document.hidden);
      };

      document.addEventListener('visibilitychange', handleVisibilityChange);
      return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, []);

    useEffect(() => {
      if (!shouldPrepareAssistant || robotAnimationReady) return;

      let mounted = true;
      const animatedRobotImage = new Image();
      animatedRobotImage.decoding = 'async';
      animatedRobotImage.setAttribute('fetchpriority', 'low');
      animatedRobotImage.onload = () => {
        if (mounted) {
          setRobotAnimationReady(true);
        }
      };
      animatedRobotImage.src = AIRobot;

      return () => {
        mounted = false;
        animatedRobotImage.onload = null;
      };
    }, [robotAnimationReady, shouldPrepareAssistant]);

    const robotIframeOpenEffect = useCallback<AssistantTransitionEffect>(
      ({ iframe, onCleanup }) => {
        onCleanup(() => iframe.style.removeProperty('will-change'));
        return animateAssistantIframeOpen(iframe, pageScale, buttonHostRef.current, false);
      },
      [pageScale]
    );
    const robotIframeCloseEffect = useCallback<AssistantTransitionEffect>(
      ({ iframe, onCleanup }) => {
        onCleanup(() => iframe.style.removeProperty('will-change'));
        return animateAssistantIframeClose(iframe, pageScale, buttonHostRef.current, false);
      },
      [pageScale]
    );

    useAssistantEmbed(
      buttonHostRef,
      pageScale,
      assistantEnabled,
      openEffect ?? robotIframeOpenEffect,
      closeEffect ?? robotIframeCloseEffect
    );

    useEffect(() => {
      if (!assistantEnabled) return;

      const onTransitionStart = (event: Event) => {
        const transitionEvent = event as CustomEvent<{ action?: 'open' | 'close' }>;
        const robot = buttonHostRef.current;
        if (!robot) return;

        if (transitionEvent.detail?.action === 'open') {
          robotOpenEffect(robot);
        }
        if (transitionEvent.detail?.action === 'close') {
          robotCloseEffect(robot);
        }
      };

      const assistantWindows = getAssistantWindows();
      assistantWindows.forEach(assistantWindow =>
        assistantWindow.addEventListener(assistantTransitionStartEvent, onTransitionStart)
      );
      return () => {
        assistantWindows.forEach(assistantWindow =>
          assistantWindow.removeEventListener(assistantTransitionStartEvent, onTransitionStart)
        );
      };
    }, [assistantEnabled, robotCloseEffect, robotOpenEffect]);

    // Portal container
    useEffect(() => {
      if (!assistantEnabled) return;

      const syncPortalContainer = () => {
        setAssistantPortalContainer(getElementPortalContainer(buttonHostRef.current));
      };
      syncPortalContainer();
      document.addEventListener('fullscreenchange', syncPortalContainer);
      return () => {
        document.removeEventListener('fullscreenchange', syncPortalContainer);
      };
    }, [assistantEnabled]);

    // ==================== Event Handlers ====================

    const openAssistant = useCallback(() => {
      syncAssistantPortalContainer();
      const controller = getAssistantEmbedController();
      if (controller) {
        void controller.toggle();
        return;
      }

      findAssistantElement<HTMLDivElement>(assistantButtonId)?.click();
    }, []);

    const onAssistantKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        openAssistant();
      }
    };

    const onAssistantPointerDown = (event: PointerEvent<HTMLDivElement>) => {
      if (event.button !== 0) return;

      const rect = event.currentTarget.getBoundingClientRect();
      const origin = assistantPosition ?? { left: rect.left, top: rect.top };
      const nextPosition = clampAssistantPosition(origin, rect.width, rect.height);

      setAssistantSize({ width: rect.width, height: rect.height });
      event.currentTarget.focus();
      event.currentTarget.setPointerCapture(event.pointerId);
      setAssistantPosition(nextPosition);
      assistantDragRef.current = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        originLeft: nextPosition.left,
        originTop: nextPosition.top,
        moved: false,
      };
    };

    const onAssistantPointerMove = (event: PointerEvent<HTMLDivElement>) => {
      const dragState = assistantDragRef.current;
      if (dragState.pointerId !== event.pointerId) return;

      const deltaX = event.clientX - dragState.startX;
      const deltaY = event.clientY - dragState.startY;
      if (!dragState.moved && Math.hypot(deltaX, deltaY) < 4) return;

      event.preventDefault();
      dragState.moved = true;
      setAssistantDragging(true);
      setAssistantPinned(true);

      const rect = event.currentTarget.getBoundingClientRect();
      setAssistantPosition(
        clampAssistantPosition(
          {
            left: dragState.originLeft + deltaX,
            top: dragState.originTop + deltaY,
          },
          rect.width,
          rect.height
        )
      );
    };

    const stopAssistantDrag = (event: PointerEvent<HTMLDivElement>) => {
      const dragState = assistantDragRef.current;
      if (dragState.pointerId !== event.pointerId) return;

      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
      assistantDragRef.current.pointerId = -1;
      setAssistantDragging(false);

      if (dragState.moved) {
        suppressAssistantClickRef.current = true;
        window.setTimeout(() => {
          suppressAssistantClickRef.current = false;
        }, 0);
      }
    };

    const onAssistantClick = () => {
      if (suppressAssistantClickRef.current) return;
      openAssistant();
    };

    // ==================== Render ====================

    if (!assistantEnabled) return null;

    const assistantButton = (
      <div
        ref={buttonHostRef}
        className={`${styles.aiRobotWrapper} ${assistantDragging ? styles.aiRobotWrapperDragging : ''} ${
          className ?? ''
        }`}
        style={
          assistantPosition
            ? {
                left: assistantPosition.left,
                top: assistantPosition.top,
                right: 'auto',
                bottom: 'auto',
                width: assistantSize?.width,
                height: assistantSize?.height,
              }
            : { visibility: 'hidden' }
        }
        role="button"
        tabIndex={0}
        onClick={onAssistantClick}
        onKeyDown={onAssistantKeyDown}
        onPointerDown={onAssistantPointerDown}
        onPointerMove={onAssistantPointerMove}
        onPointerUp={stopAssistantDrag}
        onPointerCancel={stopAssistantDrag}
      >
        <div className={styles.aiRobotMotion}>
          <img
            className={styles.aiRobot}
            src={AIRobot}
            alt="AI助手"
            draggable={false}
            loading="lazy"
            decoding="async"
          />
        </div>
        <div className={styles.aiBubble}>
          <span>Hi，点击与我交流</span>
        </div>
      </div>
    );

    return assistantPortalContainer ? createPortal(assistantButton, assistantPortalContainer) : assistantButton;
  }
);

AiAssistant.displayName = 'AiAssistant';

export default AiAssistant;
